import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useFeature } from "../context/FeatureFlagContext";
import { OPERO_LOGOS } from "@opero/ui/brand";

const navLinks: { label: string; href: string; flag?: "WORKFLOW_AUTOMATION" }[] = [
  { label: "Features", href: "#features" },
  { label: "Workflow", href: "#workflow", flag: "WORKFLOW_AUTOMATION" },
  { label: "Pricing", href: "#pricing" },
];

export function Navbar({
  onLoginClick,
  onSignupClick,
}: {
  onLoginClick?: () => void;
  onSignupClick?: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const blogEnabled = useFeature("BLOG");
  const workflowEnabled = useFeature("WORKFLOW_AUTOMATION");
  const visibleNavLinks = navLinks.filter(
    (link) => !link.flag || (link.flag === "WORKFLOW_AUTOMATION" && workflowEnabled),
  );
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = () => setMobileOpen(false);

  const resolveHref = (href: string) => {
    if (isHome) return href;
    return `/${href}`;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass shadow-lg shadow-black/5 dark:shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-400 shadow-lg shadow-primary-500/25">
            <img src={OPERO_LOGOS.mark} alt="Opero" className="h-5 w-5" />
          </div>
          <span
            className={`text-xl font-bold tracking-tight transition-colors duration-200 ${scrolled ? "text-slate-900 dark:text-white" : "text-slate-900 dark:text-white"}`}
          >
            Opero
          </span>
        </Link>

        <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
          {visibleNavLinks.map((link) => (
            <a
              key={link.href}
              href={resolveHref(link.href)}
              className={`text-sm font-medium transition-colors duration-200 ${
                scrolled
                  ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
          <Link
            to="/about"
            className={`text-sm font-medium transition-colors duration-200 ${
              scrolled
                ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
            }`}
          >
            About
          </Link>
          <Link
            to="/contact"
            className={`text-sm font-medium transition-colors duration-200 ${
              scrolled
                ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
            }`}
          >
            Contact
          </Link>
          {blogEnabled && (
            <Link
              to="/blog"
              className={`text-sm font-medium transition-colors duration-200 ${
                scrolled
                  ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
              }`}
            >
              Blog
            </Link>
          )}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <button
            onClick={onLoginClick}
            className="rounded-xl border border-primary-500/30 dark:border-primary-400/30 px-5 py-2.5 text-sm font-semibold text-primary-500 dark:text-primary-400 transition-all duration-200 hover:bg-primary-500/5 dark:hover:bg-primary-400/5"
          >
            Login
          </button>
          <button
            onClick={onSignupClick}
            className="rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-primary-500/30 hover:brightness-110"
          >
            Start Free Trial
          </button>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X
                size={24}
                weight="bold"
                className={
                  scrolled
                    ? "text-slate-700 dark:text-slate-200"
                    : "text-slate-700 dark:text-white"
                }
              />
            ) : (
              <List
                size={24}
                weight="bold"
                className={
                  scrolled
                    ? "text-slate-700 dark:text-slate-200"
                    : "text-slate-700 dark:text-white"
                }
              />
            )}
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-200 md:hidden ${
          mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div
          className={`border-t px-6 pb-4 ${scrolled ? "glass border-slate-200 dark:border-slate-700" : "glass border-slate-200 dark:border-white/10"}`}
        >
          {visibleNavLinks.map((link) => (
            <a
              key={link.href}
              href={resolveHref(link.href)}
              onClick={handleClick}
              className={`block py-3 text-sm font-medium transition-colors ${
                scrolled
                  ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
          <Link
            to="/about"
            onClick={handleClick}
            className={`block py-3 text-sm font-medium transition-colors ${
              scrolled
                ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
            }`}
          >
            About
          </Link>
          <Link
            to="/contact"
            onClick={handleClick}
            className={`block py-3 text-sm font-medium transition-colors ${
              scrolled
                ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
            }`}
          >
            Contact
          </Link>
          {blogEnabled && (
            <Link
              to="/blog"
              onClick={handleClick}
              className={`block py-3 text-sm font-medium transition-colors ${
                scrolled
                  ? "text-slate-600 hover:text-primary-500 dark:text-slate-300 dark:hover:text-primary-400"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
              }`}
            >
              Blog
            </Link>
          )}
          <button
            onClick={() => {
              handleClick();
              onLoginClick?.();
            }}
            className={`block w-full text-left py-3 text-sm font-medium transition-colors ${
              scrolled
                ? "text-primary-500 dark:text-primary-400"
                : "text-primary-500 dark:text-primary-400"
            }`}
          >
            Login
          </button>
          <button
            onClick={() => {
              handleClick();
              onSignupClick?.();
            }}
            className="mt-2 block w-full rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-2.5 text-center text-sm font-semibold text-white"
          >
            Start Free Trial
          </button>
        </div>
      </div>
    </nav>
  );
}
